import { isAddress } from 'ethers/address';
import { Interface } from 'ethers/abi';
import { parseEther } from 'ethers/utils';
/**
 * Validates multiple contract methods exist and are callable
 * @param contract - The contract instance
 * @param methodNames - Array of method names to validate
* @throws Error if any method doesn't exist or isn't callable
 *
 * @example
 * ```typescript
 * const contract = createContractReadOnly(address, abi, provider);
 *
 * // Validate multiple methods at once
 * validateContractMethods(contract, ['balanceOf', 'transfer', 'approve'],);
 * ```
 */
export function validateContractMethods(contract, methodNames) {
    const missingMethods = [];
    const invalidMethods = [];
    for (const methodName of methodNames) {
        if (!contract[methodName] || typeof contract[methodName] !== 'function') {
            missingMethods.push(methodName);
        }
        else if (contract[methodName].populateTransaction &&
            typeof contract[methodName].populateTransaction !== 'function') {
            invalidMethods.push(methodName);
        }
    }
    if (missingMethods.length > 0) {
        throw new Error(`Methods not found in contract ABI: ${missingMethods.join(', ')}`);
    }
    if (invalidMethods.length > 0) {
        throw new Error(`Invalid contract methods: ${invalidMethods.join(', ')}`);
    }
}
/**
 * Enhanced transaction parameters validation
 */
export function validateTransactionParams(params, config = {}) {
    const { requireValidAddress = true, requireNonZeroValue = false, maxGasLimit } = config;
    // Validate recipient address
    if (params.to && requireValidAddress) {
        if (!isAddress(params.to)) {
            throw new Error(`Invalid recipient address: ${params.to}`);
        }
    }
    // Validate value (handle both string and bigint)
    if (params.value !== undefined && params.value !== null) {
        try {
            let valueBigInt;
            if (typeof params.value === 'string') {
                valueBigInt = parseEther(params.value);
            }
            else {
                valueBigInt = BigInt(params.value);
            }
            if (requireNonZeroValue && valueBigInt === 0n) {
                throw new Error('Transaction value must be greater than zero');
            }
            if (valueBigInt < 0n) {
                throw new Error('Transaction value cannot be negative');
            }
        }
        catch (error) {
            throw new Error(`Invalid transaction value: ${params.value} - ${error.message}`);
        }
    }
    // Validate gas parameters
    validateGasParameters(params, maxGasLimit);
    // Validate nonce
    if (params.nonce !== undefined) {
        if (!Number.isInteger(params.nonce) || params.nonce < 0) {
            throw new Error(`Invalid nonce: ${params.nonce}`);
        }
    }
    // Validate data
    if (params.data && params.data !== '0x') {
        if (!params.data.startsWith('0x') || params.data.length % 2 !== 0) {
            throw new Error(`Invalid transaction data format: ${params.data}`);
        }
    }
}
/**
 * Validate gas-related parameters
 */
function validateGasParameters(params, maxGasLimit) {
    // Validate gas limit
    if (params.gasLimit !== undefined) {
        try {
            const gasLimitBigInt = BigInt(params.gasLimit);
            if (gasLimitBigInt <= 0n) {
                throw new Error('Gas limit must be greater than zero');
            }
            if (maxGasLimit) {
                const maxGasBigInt = BigInt(maxGasLimit);
                if (gasLimitBigInt > maxGasBigInt) {
                    throw new Error(`Gas limit ${params.gasLimit} exceeds maximum ${maxGasLimit}`);
                }
            }
        }
        catch (error) {
            throw new Error(`Invalid gas limit: ${params.gasLimit} - ${error.message}`);
        }
    }
    // Validate legacy gas price
    if (params.gasPrice !== undefined) {
        try {
            const gasPriceBigInt = BigInt(params.gasPrice);
            if (gasPriceBigInt < 0n) {
                throw new Error('Gas price cannot be negative');
            }
        }
        catch (error) {
            throw new Error(`Invalid gas price: ${params.gasPrice} - ${error.message}`);
        }
    }
    // Validate EIP-1559 fee parameters
    if (params.maxFeePerGas !== undefined) {
        try {
            const maxFeeBigInt = BigInt(params.maxFeePerGas);
            if (maxFeeBigInt < 0n) {
                throw new Error('Max fee per gas cannot be negative');
            }
        }
        catch (error) {
            throw new Error(`Invalid max fee per gas: ${params.maxFeePerGas} - ${error.message}`);
        }
    }
    if (params.maxPriorityFeePerGas !== undefined) {
        try {
            const maxPriorityFeeBigInt = BigInt(params.maxPriorityFeePerGas);
            if (maxPriorityFeeBigInt < 0n) {
                throw new Error('Max priority fee per gas cannot be negative');
            }
            // Validate that priority fee doesn't exceed max fee
            if (params.maxFeePerGas !== undefined) {
                const maxFeeBigInt = BigInt(params.maxFeePerGas);
                if (maxPriorityFeeBigInt > maxFeeBigInt) {
                    throw new Error('Max priority fee cannot exceed max fee per gas');
                }
            }
        }
        catch (error) {
            throw new Error(`Invalid max priority fee per gas: ${params.maxPriorityFeePerGas} - ${error.message}`);
        }
    }
}
/**
 * Validate contract function parameters against actual ABI
 * @param contract - Ethers contract instance with ABI
 * @param functionName - Function name to validate
 * @param params - Parameters to validate
 */
export function validateContractParamsFromABI(contract, functionName, params) {
    try {
        // Get the function fragment from the contract's interface
        const functionFragment = contract.interface.getFunction(functionName);
        if (!functionFragment) {
            throw new Error(`Function ${functionName} not found in contract ABI`);
        }
        // Check parameter count
        if (params.length !== functionFragment.inputs.length) {
            throw new Error(`Function ${functionName} expects ${functionFragment.inputs.length} parameters, got ${params.length}`);
        }
        // Validate each parameter type
        for (let i = 0; i < params.length; i++) {
            const param = params[i];
            const input = functionFragment.inputs[i];
            validateParameterType(param, input.type, `${functionName} parameter ${i} (${input.name})`);
        }
        // Test encode to catch any remaining issues
        contract.interface.encodeFunctionData(functionName, params);
    }
    catch (error) {
        throw new Error(`Parameter validation failed for ${functionName}: ${error.message}`);
    }
}
/**
 * Enhanced parameter type validation with better error handling
 */
export function validateParameterType(value, solidityType, paramDescription) {
    if (value === null || value === undefined) {
        throw new Error(`${paramDescription} cannot be null or undefined`);
    }
    try {
        switch (true) {
            case solidityType === 'address':
                if (!isAddress(String(value))) {
                    throw new Error(`${paramDescription} must be a valid address, got: ${value}`);
                }
                break;
            case solidityType.startsWith('uint'):
                const uintValue = BigInt(value);
                if (uintValue < 0n) {
                    throw new Error(`${paramDescription} must be non-negative, got: ${value}`);
                }
                // Check bit size (uint8, uint256, etc.)
                const bitSize = solidityType === 'uint' ? 256 : parseInt(solidityType.substring(4));
                const maxValue = 2n ** BigInt(bitSize) - 1n;
                if (uintValue > maxValue) {
                    throw new Error(`${paramDescription} exceeds maximum value for ${solidityType}`);
                }
                break;
            case solidityType.startsWith('int'):
                const intValue = BigInt(value);
                const intBitSize = solidityType === 'int' ? 256 : parseInt(solidityType.substring(3));
                const maxIntValue = 2n ** BigInt(intBitSize - 1) - 1n;
                const minIntValue = -(2n ** BigInt(intBitSize - 1));
                if (intValue > maxIntValue || intValue < minIntValue) {
                    throw new Error(`${paramDescription} out of range for ${solidityType}`);
                }
                break;
            case solidityType === 'bool':
                if (typeof value !== 'boolean') {
                    throw new Error(`${paramDescription} must be a boolean, got: ${typeof value}`);
                }
                break;
            case solidityType === 'string':
                if (typeof value !== 'string') {
                    throw new Error(`${paramDescription} must be a string, got: ${typeof value}`);
                }
                break;
            case solidityType.startsWith('bytes'):
                if (typeof value !== 'string' || !value.startsWith('0x')) {
                    throw new Error(`${paramDescription} must be valid bytes (0x...), got: ${value}`);
                }
                // Check fixed bytes length (bytes32, bytes4, etc.)
                if (solidityType !== 'bytes') {
                    const expectedLength = parseInt(solidityType.substring(5));
                    const actualLength = (value.length - 2) / 2; // Remove 0x and convert hex to bytes
                    if (actualLength !== expectedLength) {
                        throw new Error(`${paramDescription} must be exactly ${expectedLength} bytes, got ${actualLength}`);
                    }
                }
                break;
            case solidityType.endsWith('[]'):
                if (!Array.isArray(value)) {
                    throw new Error(`${paramDescription} must be an array, got: ${typeof value}`);
                }
                // Recursively validate array elements
                const elementType = solidityType.slice(0, -2);
                value.forEach((item, index) => {
                    validateParameterType(item, elementType, `${paramDescription}[${index}]`);
                });
                break;
            case solidityType.includes('[') && solidityType.includes(']'):
                // Handle fixed-size arrays like uint256[5]
                if (!Array.isArray(value)) {
                    throw new Error(`${paramDescription} must be an array, got: ${typeof value}`);
                }
                const match = solidityType.match(/(.+)\[(\d+)\]$/);
                if (match) {
                    const [, baseType, sizeStr] = match;
                    const expectedSize = parseInt(sizeStr);
                    if (value.length !== expectedSize) {
                        throw new Error(`${paramDescription} must have exactly ${expectedSize} elements, got ${value.length}`);
                    }
                    value.forEach((item, index) => {
                        validateParameterType(item, baseType, `${paramDescription}[${index}]`);
                    });
                }
                break;
            default:
                // Handle tuple/struct types
                if (solidityType.startsWith('tuple') || solidityType.includes('(')) {
                    // For complex types, rely on ethers encoding validation
                    break;
                }
                throw new Error(`Unsupported Solidity type: ${solidityType}`);
        }
    }
    catch (error) {
        if (error.message.includes('Type validation failed')) {
            throw error; // Re-throw our own errors
        }
        throw new Error(`Type validation failed for ${paramDescription}: ${error.message}`);
    }
}
/**
 * Validate network and provider configuration
 * @param provider - Ethers provider instance
 * @param expectedChainId - Expected network chain ID
 */
export async function validateNetwork(provider, expectedChainId) {
    try {
        const network = await provider.getNetwork();
        if (expectedChainId && Number(network.chainId) !== expectedChainId) {
            throw new Error(`Network mismatch: expected chain ID ${expectedChainId}, got ${network.chainId}`);
        }
    }
    catch (error) {
        throw new Error(`Failed to validate network: ${error.message}`);
    }
}
/**
 * Enhanced contract method validation with parameter checking
 */
export function validateContractMethodWithParams(contract, methodName, params) {
    // First validate the method exists
    validateContractMethods(contract, [methodName]);
    // Then validate the parameters
    validateContractParamsFromABI(contract, methodName, params);
}
/**
 * Safely call contract method with automatic parameter validation
 */
export async function safeContractCall(contract, methodName, params = [], options = {}) {
    const { validate = true, dryRun = false } = options;
    try {
        // Validate method exists
        validateContractMethods(contract, [methodName]);
        // Validate parameters against ABI if requested
        if (validate) {
            validateContractParamsFromABI(contract, methodName, params);
        }
        // Dry run to catch errors before actual execution
        if (dryRun) {
            try {
                await contract[methodName].staticCall(...params);
            }
            catch (error) {
                throw new Error(`Dry run failed: ${error.message}`);
            }
        }
        // Execute the actual call
        return await contract[methodName](...params);
    }
    catch (error) {
        throw new Error(`Contract call failed for ${methodName}: ${error.message}`);
    }
}
/**
 * Pre-flight validation for contract transactions
 */
export async function validateContractTransaction(contract, methodName, params, options = {}) {
    const { estimateGas = false, dryRun = true } = options;
    try {
        // Validate method and parameters
        validateContractMethodWithParams(contract, methodName, params);
        // Dry run if requested
        if (dryRun) {
            await contract[methodName].staticCall(...params);
        }
        // Gas estimation if requested
        let gasEstimate;
        if (estimateGas) {
            gasEstimate = await contract[methodName].estimateGas(...params);
        }
        return { gasEstimate, valid: true };
    }
    catch (error) {
        throw new Error(`Transaction validation failed: ${error.message}`);
    }
}
/**
 * Validate contract deployment bytecode
 * @param bytecode - Contract bytecode to validate
 */
export function validateContractBytecode(bytecode) {
    if (!bytecode || typeof bytecode !== 'string') {
        throw new Error('Contract bytecode must be a non-empty string');
    }
    if (!bytecode.startsWith('0x')) {
        throw new Error('Contract bytecode must start with 0x');
    }
    if (bytecode.length <= 2) {
        throw new Error('Contract bytecode cannot be empty');
    }
    // Check if bytecode is valid hex
    const hexPattern = /^0x[0-9a-fA-F]*$/;
    if (!hexPattern.test(bytecode)) {
        throw new Error('Contract bytecode must be valid hexadecimal');
    }
}
/**
 * Clean input/output parameters for functions, constructors, errors
 * Removes properties that are invalid for non-event parameters
 */
function cleanInputsOrOutputs(params) {
    if (!Array.isArray(params)) {
        return [];
    }
    return params.map((param) => {
        if (!param || typeof param !== 'object') {
            return param;
        }
        const cleanParam = {
            name: param.name ?? '',
            type: param.type ?? 'uint256', // Default to uint256 if type is missing (shouldn't happen)
        };
        // Handle tuple types with components
        if (param.components && Array.isArray(param.components)) {
            cleanParam.components = cleanInputsOrOutputs(param.components);
        }
        // Preserve internalType only if it doesn't cause issues
        // (some malformed internalTypes can break ethers)
        if (param.internalType && typeof param.internalType === 'string') {
            cleanParam.internalType = param.internalType;
        }
        // NOTE: 'indexed' is intentionally NOT included here
        // It's only valid for event parameters
        return cleanParam;
    });
}
/**
 * Clean an ABI to be compatible with Ethers.js v6
 * Handles common Web3.js ABI format incompatibilities:
 * - Removes 'indexed' from function inputs (only valid for event parameters)
 * - Removes 'indexed' from function outputs
 * - Removes 'internalType' if it causes parsing issues
 * - Ensures 'stateMutability' is valid
 * - Removes unknown/invalid properties that cause ethers to drop fragments
 */
function cleanABI(abi) {
    return abi.map(fragment => {
        // Skip non-object fragments
        if (!fragment || typeof fragment !== 'object') {
            return fragment;
        }
        const cleanFragment = {
            type: fragment.type,
            name: fragment.name,
        };
        // Handle functions
        if (fragment.type === 'function') {
            // Clean inputs - remove 'indexed' (only valid for events)
            if (fragment.inputs) {
                cleanFragment.inputs = cleanInputsOrOutputs(fragment.inputs);
            }
            // Clean outputs - remove 'indexed'
            if (fragment.outputs) {
                cleanFragment.outputs = cleanInputsOrOutputs(fragment.outputs);
            }
            // Preserve valid function properties
            if (fragment.stateMutability) {
                cleanFragment.stateMutability = fragment.stateMutability;
            }
            // Handle legacy 'constant' and 'payable' for older ABIs
            if (fragment.constant !== undefined && !fragment.stateMutability) {
                cleanFragment.stateMutability = fragment.constant ? 'view' : 'nonpayable';
            }
            if (fragment.payable && !fragment.stateMutability) {
                cleanFragment.stateMutability = 'payable';
            }
        }
        // Handle events - 'indexed' IS valid here
        else if (fragment.type === 'event') {
            cleanFragment.anonymous = fragment.anonymous ?? false;
            if (fragment.inputs) {
                cleanFragment.inputs = fragment.inputs.map((input) => ({
                    name: input.name ?? '',
                    type: input.type,
                    indexed: input.indexed ?? false,
                    // Keep internalType for events if present
                    ...(input.internalType && { internalType: input.internalType })
                }));
            }
        }
        // Handle constructor
        else if (fragment.type === 'constructor') {
            if (fragment.inputs) {
                cleanFragment.inputs = cleanInputsOrOutputs(fragment.inputs);
            }
            if (fragment.stateMutability) {
                cleanFragment.stateMutability = fragment.stateMutability;
            }
        }
        // Handle fallback/receive
        else if (fragment.type === 'fallback' || fragment.type === 'receive') {
            if (fragment.stateMutability) {
                cleanFragment.stateMutability = fragment.stateMutability;
            }
        }
        // Handle errors
        else if (fragment.type === 'error') {
            if (fragment.inputs) {
                cleanFragment.inputs = cleanInputsOrOutputs(fragment.inputs);
            }
        }
        // Pass through unknown types unchanged
        else {
            return fragment;
        }
        return cleanFragment;
    });
}
/**
 * Converts various ABI formats to ethers Interface with automatic cleaning for compatibility
 * @param abi - ABI in various formats (Interface, string, array, object)
 * @returns Ethers Interface instance
 * @throws Error if ABI format is invalid
 *
 * @example
 * ```typescript
 * // From array
 * const interface1 = convertAbiToInterface(ERC20_ABI);
 *
 * // From JSON string
 * const interface2 = convertAbiToInterface('[{"type":"function","name":"transfer",...}]');
 *
 * // Already an Interface
 * const interface3 = convertAbiToInterface(existingInterface);
 * ```
 */
export function convertAbiToInterface(abi) {
    try {
        // Handle null/undefined
        if (!abi) {
            throw new Error('ABI is required');
        }
        // Already an Interface
        if (abi instanceof Interface) {
            return abi;
        }
        // String format (JSON)
        let abiArray;
        if (typeof abi === 'string') {
            abiArray = JSON.parse(abi);
        }
        else if (Array.isArray(abi)) {
            abiArray = abi;
        }
        else if (typeof abi === 'object') {
            // Single fragment object
            abiArray = [abi];
        }
        else {
            throw new Error(`Unsupported ABI type: ${typeof abi}`);
        }
        // Clean the ABI
        const cleanedAbi = cleanABI(abiArray);
        // Try to create Interface and validate all fragments were parsed
        const iface = new Interface(cleanedAbi);
        // Validation: Check if expected functions are present
        const expectedFunctions = cleanedAbi
            .filter((f) => f.type === 'function')
            .map((f) => f.name);
        const actualFunctions = iface.fragments
            .filter(f => f.type === 'function')
            .map(f => f.name);
        const droppedFunctions = expectedFunctions.filter((name) => !actualFunctions.includes(name));
        if (droppedFunctions.length > 0) {
            console.warn(`[web3-ts] Warning: ${droppedFunctions.length} function(s) were dropped during ABI conversion:`, droppedFunctions.join(', '));
        }
        return iface;
    }
    catch (error) {
        if (error.message.includes('JSON')) {
            throw new Error('Invalid JSON format in ABI string');
        }
        if (error.message.includes('invalid fragment') ||
            error.message.includes('parameter cannot be indexed')) {
            console.error('[web3-ts] ABI fragment error:', error.message);
            throw new Error(`Invalid ABI fragment structure: ${error.message}`);
        }
        throw error;
    }
}
